/**
 * Drop-in replacement for validate.js (CVE-2020-26308).
 * Implements only the API and rules used in this project.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function stripMessage(message) {
  if (typeof message === "string" && message.charAt(0) === "^") {
    return message.slice(1);
  }
  return message;
}

function isEmpty(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function runFieldValidators(value, constraint, attributes, attribute) {
  const errors = [];
  if (!constraint) return errors;

  for (const validatorName of Object.keys(constraint)) {
    if (validatorName === "presence") {
      const opts = constraint.presence;
      const allowEmpty = opts && opts.allowEmpty;
      if (!allowEmpty && isEmpty(value)) {
        errors.push(stripMessage(opts.message || "is required"));
      }
    } else if (validatorName === "format") {
      const opts = constraint.format;
      if (opts && opts.pattern && !isEmpty(value)) {
        const regex = new RegExp(opts.pattern, opts.flags || "");
        if (!regex.test(value)) {
          errors.push(stripMessage(opts.message || "is invalid"));
        }
      }
    } else if (validatorName === "email") {
      const opts = constraint.email;
      if (!isEmpty(value) && !EMAIL_REGEX.test(value)) {
        errors.push(stripMessage(opts.message || "is invalid"));
      }
    } else if (validate.validators[validatorName]) {
      const result = validate.validators[validatorName](
        value,
        constraint[validatorName],
        attribute,
        attributes,
      );
      if (result) {
        errors.push(stripMessage(result));
      }
    }
  }

  return errors;
}

function validateAttributes(values, constraints) {
  const errors = {};
  let hasErrors = false;

  for (const attribute of Object.keys(constraints)) {
    const value = values[attribute];
    const fieldErrors = runFieldValidators(
      value,
      constraints[attribute],
      values,
      attribute,
    );
    if (fieldErrors.length > 0) {
      errors[attribute] = fieldErrors;
      hasErrors = true;
    }
  }

  return hasErrors ? errors : undefined;
}

function collectFormValues(form, modifier) {
  const values = {};
  if (!form || !form.querySelectorAll) return values;

  form.querySelectorAll("input, select, textarea").forEach((element) => {
    const name = element.name;
    if (!name) return;

    if (element.type === "checkbox") {
      values[name] = element.checked ? element.value : "";
      return;
    }

    if (element.type === "radio") {
      if (element.checked) {
        values[name] = element.value;
      }
      return;
    }

    let value = element.value;
    if (modifier === "trim" && typeof value === "string") {
      value = value.trim();
    }
    values[name] = value;
  });

  return values;
}

function validate(values, constraints) {
  return validateAttributes(values, constraints);
}

validate.collectFormValues = collectFormValues;
validate.validators = {};

export default validate;
